/**
 * tRPC Client
 *
 * Provides typed API access to the backend server.
 * Uses vanilla client (not React Query) for simpler integration.
 */

import { createTRPCProxyClient, httpBatchLink } from '@trpc/client';
import type { AppRouter } from '../../../api/src/router';
import { useCampaignStore } from '../stores/campaignStore';
import { supabase } from './supabase';

// =============================================================================
// CLIENT
// =============================================================================

/**
 * tRPC client instance
 *
 * In development: Uses Vite's proxy to forward /trpc to localhost:3334
 * In production: Calls the Render API directly
 */
const API_URL = import.meta.env.VITE_API_URL || '';

export const trpc = createTRPCProxyClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${API_URL}/trpc`,
      async headers() {
        const campaignId = useCampaignStore.getState().currentCampaignId;
        const headers: Record<string, string> = {};

        // Get a fresh session (triggers token refresh if expired)
        const { data: { session } } = await supabase.auth.getSession();

        // Send the Supabase JWT for server-side verification (JWKS/ES256). This
        // is the ONLY auth signal — the legacy x-user-id header was removed in
        // EPIC-0034 (it let a client assert its own identity).
        if (session?.access_token) {
          headers['Authorization'] = `Bearer ${session.access_token}`;
        }

        // Campaign context (not auth — just scoping)
        if (campaignId) {
          headers['x-campaign-id'] = campaignId;
        }

        return headers;
      },
    }),
  ],
});

// =============================================================================
// RE-EXPORT TYPE
// =============================================================================

export type { AppRouter };
